import Vue from "vue";

import { api } from "@tt-app/api";
import {
  IsDrillHandler,
  DrillState,
  DrillAnswer,
  DrillListeningAnswer,
  DrillPronunciationAnswer,
  ExerciseState,
  RespDrillAnswer,
  PayloadDrillPronunciationAnswer,
  PayloadDrillListeningAnswer,
  PayloadDrillSkippedAnswer,
  DrillUserAnswer,
  DrillQuestionAndAnswers,
  DrillLearningStatus,
  DrillPronunciationPhonemeEvaluation,
  DrillVocabularyAnswer,
  DrillDisplay
} from "@tt-app/models/exercise";
import { BaseHandler, exerciseHandler } from "../common";
import { findDrillQuestion } from "./misc";

class DrillHandler extends BaseHandler implements IsDrillHandler {
  // --------------------------------------------------------------------------
  //  Initialisation
  // --------------------------------------------------------------------------
  constructor() {
    super();
  }

  // --------------------------------------------------------------------------
  //  Methods
  // --------------------------------------------------------------------------
  getNewState(): DrillState {
    return {
      currentIndex: -1,
      data: undefined,
      isFinished: false,
      display: "idle",
      exercises: [],
      practiceMode: false,
      totalNewLearnedBadge: 0
    };
  }

  async loadData<V extends Vue>(
    component: V,
    state: DrillState,
    exerciseState: ExerciseState,
    forceReload: boolean
  ): Promise<DrillState> {
    if (!forceReload && state.data) {
      this._info(`Chat.loadData: data already loaded, skipping...`);
      return state;
    }
    // Fetch data
    const info = await exerciseHandler.getAuthExerciseInfo(component, exerciseState);
    const data = await api.exercise.getDrillBasedExercise(info);

    let exercises: DrillQuestionAndAnswers[] = [];
    for (const page of data.exercise.pages) {
      const drillQuestion = findDrillQuestion(page.body);
      if (drillQuestion) {
        exercises = [
          ...exercises,
          { question: drillQuestion, lastAnswer: undefined, allAnswers: [] }
        ];
      }
    }

    let display: DrillDisplay;
    if ((component as any).limitedModeActive) {
      display = "drill-question";
    } else {
      display = "exercise-scope";
    }

    return {
      // Start from 0
      currentIndex: 0,
      data,
      isFinished: false,
      // Start right away, no intro
      display,
      exercises,
      practiceMode: false,
      totalNewLearnedBadge: 0
    };
  }

  async loadPracticeData<V extends Vue>(
    component: V,
    state: DrillState,
    exerciseState: ExerciseState
  ) {
    const info = await exerciseHandler.getAuthExerciseInfo(component, exerciseState);
    const data = await api.exercise.getDrillBasedPractice(info);

    let exercises: DrillQuestionAndAnswers[] = [];
    for (const page of data.exercise.pages) {
      const drillQuestion = findDrillQuestion(page.body);
      if (drillQuestion) {
        exercises = [
          ...exercises,
          { question: drillQuestion, lastAnswer: undefined, allAnswers: [] }
        ];
      }
    }

    return {
      // Start from 0
      currentIndex: 0,
      data,
      isFinished: false,
      // Start right away, no intro
      display: "drill-question",
      exercises,
      totalNewLearnedBadge: 0
    };
  }

  moveToPage(state: DrillState, pageIndex?: number): DrillState {
    // No data loaded
    if (!state.data) {
      return state;
    }

    const hasExerciseScope = state.data.exercise.pages[0].body[0].type === "EXERCISE_SCOPE";

    if (state.practiceMode) {
      const targetIndex = pageIndex !== undefined ? pageIndex : state.currentIndex + 1;
      const testingIndex = targetIndex + 1;

      const pages = state.data.exercise.practicePages || [];
      if (targetIndex < 0 || targetIndex >= pages.length) {
        return state;
      }

      // Jumping to a question page
      const drillQuestion = findDrillQuestion(pages[testingIndex].body);

      if (drillQuestion) {
        return {
          ...state,
          currentIndex: targetIndex,
          display: state.isFinished ? "drill-answered" : "drill-question"
        };
      }

      // Check if it is a review of trophy page
      for (const comp of pages[testingIndex].body) {
        if (comp.type === "DRILL_REVIEW") {
          return {
            ...state,
            currentIndex: targetIndex,
            display: "drill-review"
          };
        } else if (comp.type === "DRILL_TROPHY") {
          return {
            ...state,
            currentIndex: targetIndex,
            display: "trophy",
            isFinished: true
          };
        }
      }

      // Unknown case
      return {
        ...state
      };
    } else {
      const targetIndex = pageIndex !== undefined ? pageIndex : state.currentIndex + 1;
      let testingIndex = targetIndex;
      if (hasExerciseScope) {
        testingIndex += 1;
      }

      if (targetIndex < 0 || targetIndex >= state.data.exercise.pages.length) {
        return state;
      }

      // Jumping to a question page
      const drillQuestion = findDrillQuestion(state.data.exercise.pages[testingIndex].body);

      if (drillQuestion) {
        return {
          ...state,
          currentIndex: targetIndex,
          display: state.isFinished ? "drill-answered" : "drill-question"
        };
      }

      // Check if it is a review of trophy page
      for (const comp of state.data.exercise.pages[testingIndex].body) {
        if (comp.type === "DRILL_REVIEW") {
          return {
            ...state,
            currentIndex: targetIndex,
            display: "drill-review"
          };
        } else if (comp.type === "DRILL_TROPHY") {
          return {
            ...state,
            currentIndex: targetIndex,
            display: "trophy",
            isFinished: true
          };
        }
      }

      // Unknown case
      return {
        ...state
      };
    }
  }

  async onUserSkipped<V extends Vue>(
    component: V,
    exerciseState: ExerciseState,
    state: DrillState
  ): Promise<DrillState> {
    // --- update the exercise
    const exercise = state.exercises[state.currentIndex];
    if (!exercise) {
      return state; // Edge case
    }

    // --- submit the "skipped" answer to the server
    const authInfo = await exerciseHandler.getAuthExerciseInfo(component, exerciseState);
    const payload: PayloadDrillSkippedAnswer = {
      ...authInfo,
      logId: exerciseState.logId,
      uuid: exercise.question.uuid,
      skipped: true,
      retry: exercise.question.type === "DRILL_PRONUNCIATION" ? 0 : undefined
    };
    const skippedResponse = await api.exercise.submitDrillSkippedAnswer(payload);

    exercise.skipped = skippedResponse;

    // --- directly move the next page
    return this.moveToPage(state);
  }

  async onUserSkippedPractice<V extends Vue>(
    component: V,
    exerciseState: ExerciseState,
    state: DrillState
  ) {
    // --- update the exercise
    const exercise = state.exercises[state.currentIndex];
    if (!exercise) {
      return state; // Edge case
    }

    // --- submit the "skipped" answer to the server
    const authInfo = await exerciseHandler.getAuthExerciseInfo(component, exerciseState);
    const payload: PayloadDrillSkippedAnswer = {
      ...authInfo,
      logId: exerciseState.logId,
      uuid: exercise.question.uuid,
      skipped: true,
      retry: exercise.question.type === "DRILL_PRONUNCIATION" ? 0 : undefined
    };
    exercise.skipped = await api.exercise.submitDrillPracticeSkippedAnswer(payload);

    // --- directly move the next page
    return this.moveToPage(state);
  }

  async onUserSubmit<V extends Vue>(
    component: V,
    exerciseState: ExerciseState,
    state: DrillState,
    answer: DrillAnswer
  ): Promise<DrillState> {
    // --- answer processing
    let correction: RespDrillAnswer;
    switch (answer.type) {
      case "DRILL_LISTENING":
        correction = await this.onUserSubmitListening(
          component,
          exerciseState,
          state,
          answer as DrillListeningAnswer
        );
        break;
      case "DRILL_PRONUNCIATION":
        correction = await this.onUserSubmitPronunciation(
          component,
          exerciseState,
          state,
          answer as DrillPronunciationAnswer
        );
        break;
      case "DRILL_VOCABULARY":
        correction = await this.onUserSubmitVocabulary(
          component,
          exerciseState,
          state,
          answer as DrillVocabularyAnswer
        );
        break;
      default:
        throw new Error(`Unsupported drill answer: ${JSON.stringify(answer)}`);
    }

    state.display = "drill-answered";

    // --- update the exercise
    const exercise = state.exercises.find(e => e.question.uuid === answer.uuid);
    if (!exercise) {
      // MAYDAY MAYDAY!
      return state;
    }

    // --- update question learning status
    const lastLearningStatus: DrillLearningStatus = exercise.lastAnswer
      ? exercise.lastAnswer.learnStatus.newLearnStatus
      : exercise.question.learnedStatus;

    let newLearnStatus: DrillLearningStatus = lastLearningStatus;
    let hasIncreased: boolean | undefined = undefined;

    if (correction.isCorrect) {
      switch (lastLearningStatus) {
        case null:
        case -1:
        case 0:
          hasIncreased = true;
          newLearnStatus = 1;
          break;
        case 1:
          hasIncreased = true;
          newLearnStatus = 2;
          break;
        case 2:
          hasIncreased = true;
          newLearnStatus = 3;
          break;
        case 3:
          newLearnStatus = 3;
          break;
      }
    } else {
      switch (lastLearningStatus) {
        case null:
        case -1:
        case 0:
          newLearnStatus = 0;
          break;
        case 1:
          hasIncreased = false;
          newLearnStatus = 0;
          break;
        case 2:
          hasIncreased = false;
          newLearnStatus = 1;
          break;
        case 3:
          hasIncreased = false;
          newLearnStatus = 2;
          break;
      }
    }

    const hasNewLearnedBadge = lastLearningStatus !== 3 && newLearnStatus === 3;

    const userAnswer: DrillUserAnswer = {
      submittedAnswer: answer,
      learnStatus: {
        newLearnStatus,
        hasNewLearnedBadge,
        hasIncreased
      },
      correction: correction
    };

    exercise.lastAnswer = userAnswer;
    exercise.allAnswers = [...exercise.allAnswers, userAnswer];

    // --- update global total new learned badge count: if the word was initially
    //     already LEARNED, then ignore changes
    if (exercise.question.learnedStatus !== 3) {
      // User has earned a new badge!
      if (hasNewLearnedBadge) {
        state.totalNewLearnedBadge++;
      }
      // User performance is lower than LEARNED
      // Decrease badge count only if it was previously earned:
      //  2 -> 3 -> 2   ==> will decrease the badge count by one
      //  3 -> 2        ==> does not decrease badge
      else if ((newLearnStatus === null || newLearnStatus < 3) && lastLearningStatus == 3) {
        state.totalNewLearnedBadge--;
      }
    }

    return state;
  }

  async onUserPracticeSubmit<V extends Vue>(
    component: V,
    exerciseState: ExerciseState,
    state: DrillState,
    answer: DrillAnswer
  ): Promise<DrillState> {
    // --- answer processing
    let correction: RespDrillAnswer;
    switch (answer.type) {
      case "DRILL_LISTENING":
        correction = await this.onUserSubmitListeningPractice(
          component,
          exerciseState,
          state,
          answer as DrillListeningAnswer
        );
        break;
      case "DRILL_PRONUNCIATION":
        correction = await this.onUserSubmitPronunciationPractice(
          component,
          exerciseState,
          state,
          answer as DrillPronunciationAnswer
        );
        break;
      case "DRILL_VOCABULARY":
        correction = await this.onUserSubmitVocabularyPractice(
          component,
          exerciseState,
          state,
          answer as DrillVocabularyAnswer
        );
        break;
      default:
        throw new Error(`Unsupported drill answer: ${JSON.stringify(answer)}`);
    }

    state.display = "drill-answered";

    // --- update the exercise
    const exercise = state.exercises.find(e => e.question.uuid === answer.uuid);
    if (!exercise) {
      // MAYDAY MAYDAY!
      return state;
    }

    // --- update question learning status
    const lastLearningStatus: DrillLearningStatus = exercise.lastAnswer
      ? exercise.lastAnswer.learnStatus.newLearnStatus
      : exercise.question.learnedStatus;

    let newLearnStatus: DrillLearningStatus = lastLearningStatus;
    let hasIncreased: boolean | undefined = undefined;

    if (correction.isCorrect) {
      switch (lastLearningStatus) {
        case null:
        case -1:
        case 0:
          hasIncreased = true;
          newLearnStatus = 1;
          break;
        case 1:
          hasIncreased = true;
          newLearnStatus = 2;
          break;
        case 2:
          hasIncreased = true;
          newLearnStatus = 3;
          break;
        case 3:
          newLearnStatus = 3;
          break;
      }
    } else {
      switch (lastLearningStatus) {
        case null:
        case -1:
        case 0:
          newLearnStatus = 0;
          break;
        case 1:
          hasIncreased = false;
          newLearnStatus = 0;
          break;
        case 2:
          hasIncreased = false;
          newLearnStatus = 1;
          break;
        case 3:
          hasIncreased = false;
          newLearnStatus = 2;
          break;
      }
    }

    const hasNewLearnedBadge = lastLearningStatus !== 3 && newLearnStatus === 3;

    const userAnswer: DrillUserAnswer = {
      submittedAnswer: answer,
      learnStatus: {
        newLearnStatus,
        hasNewLearnedBadge,
        hasIncreased
      },
      correction: correction
    };

    exercise.lastAnswer = userAnswer;
    exercise.allAnswers = [...exercise.allAnswers, userAnswer];

    // --- update global total new learned badge count: if the word was initially
    //     already LEARNED, then ignore changes
    if (exercise.question.learnedStatus !== 3) {
      // User has earned a new badge!
      if (hasNewLearnedBadge) {
        state.totalNewLearnedBadge++;
      }
      // User performance is lower than LEARNED
      // Decrease badge count only if it was previously earned:
      //  2 -> 3 -> 2   ==> will decrease the badge count by one
      //  3 -> 2        ==> does not decrease badge
      else if ((newLearnStatus === null || newLearnStatus < 3) && lastLearningStatus == 3) {
        state.totalNewLearnedBadge--;
      }
    }

    return state;
  }

  async onUserSubmitListening<V extends Vue>(
    component: V,
    exerciseState: ExerciseState,
    state: DrillState,
    answer: DrillListeningAnswer
  ): Promise<RespDrillAnswer> {
    const authInfo = await exerciseHandler.getAuthExerciseInfo(component, exerciseState);
    const payload: PayloadDrillListeningAnswer = {
      ...authInfo,
      uuid: answer.uuid,
      logId: exerciseState.logId,
      answer: answer.optionId
    };

    const correction = await api.exercise.submitDrillListeningAnswer(payload);

    return correction;
  }

  async onUserSubmitListeningPractice<V extends Vue>(
    component: V,
    exerciseState: ExerciseState,
    state: DrillState,
    answer: DrillListeningAnswer
  ): Promise<RespDrillAnswer> {
    const authInfo = await exerciseHandler.getAuthExerciseInfo(component, exerciseState);
    const payload: PayloadDrillListeningAnswer = {
      ...authInfo,
      uuid: answer.uuid,
      logId: exerciseState.logId,
      answer: answer.optionId
    };

    const correction = await api.exercise.submitDrillListeningPracticeAnswer(payload);

    return correction;
  }

  async onUserSubmitPronunciation<V extends Vue>(
    component: V,
    exerciseState: ExerciseState,
    state: DrillState,
    answer: DrillPronunciationAnswer
  ): Promise<RespDrillAnswer> {
    const authInfo = await exerciseHandler.getAuthExerciseInfo(component, exerciseState);

    /**
     * THe number of "Retry" equals the number of already submitted answers: if the user
     * has submitted two answers, the next submission will be the 2nd retry
     */
    const retryCount = state.exercises[state.currentIndex].allAnswers.length;

    // If the maximum PETT error count is reached, log it and consider everything
    // as incorrect
    if (answer.pettErrorCount) {
      const payload: PayloadDrillPronunciationAnswer = {
        ...authInfo,
        uuid: answer.uuid,
        logId: exerciseState.logId,
        audio: answer.audioUrl,
        pettFailed: true,
        retry: retryCount
      };

      const correction = await api.exercise.submitDrillPronunciationAnswer(payload);
      return correction;
    }

    const payload: PayloadDrillPronunciationAnswer = {
      ...authInfo,
      uuid: answer.uuid,
      logId: exerciseState.logId,
      evaluation: answer.evaluation.map(
        (ev): DrillPronunciationPhonemeEvaluation => ({
          ipa: ev.arpabet || ev.ipa,
          isCorrect: ev.isCorrect
        })
      ),
      audio: answer.audioUrl,
      retry: retryCount
    };

    const correction = await api.exercise.submitDrillPronunciationAnswer(payload);

    return correction;
  }

  async onUserSubmitPronunciationPractice<V extends Vue>(
    component: V,
    exerciseState: ExerciseState,
    state: DrillState,
    answer: DrillPronunciationAnswer
  ): Promise<RespDrillAnswer> {
    const authInfo = await exerciseHandler.getAuthExerciseInfo(component, exerciseState);

    /**
     * THe number of "Retry" equals the number of already submitted answers: if the user
     * has submitted two answers, the next submission will be the 2nd retry
     */
    const retryCount = state.exercises[state.currentIndex].allAnswers.length;

    // If the maximum PETT error count is reached, log it and consider everything
    // as incorrect
    if (answer.pettErrorCount) {
      const payload: PayloadDrillPronunciationAnswer = {
        ...authInfo,
        uuid: answer.uuid,
        logId: exerciseState.logId,
        audio: answer.audioUrl,
        pettFailed: true,
        retry: retryCount
      };

      const correction = await api.exercise.submitDrillPronunciationPracticeAnswer(payload);
      return correction;
    }

    const payload: PayloadDrillPronunciationAnswer = {
      ...authInfo,
      uuid: answer.uuid,
      logId: exerciseState.logId,
      evaluation: answer.evaluation.map(
        (ev): DrillPronunciationPhonemeEvaluation => ({
          ipa: ev.arpabet || ev.ipa,
          isCorrect: ev.isCorrect
        })
      ),
      audio: answer.audioUrl,
      retry: retryCount
    };

    const correction = await api.exercise.submitDrillPronunciationPracticeAnswer(payload);

    return correction;
  }

  async onUserSubmitVocabulary<V extends Vue>(
    component: V,
    exerciseState: ExerciseState,
    state: DrillState,
    answer: DrillVocabularyAnswer
  ): Promise<RespDrillAnswer> {
    const authInfo = await exerciseHandler.getAuthExerciseInfo(component, exerciseState);
    const payload: PayloadDrillListeningAnswer = {
      ...authInfo,
      uuid: answer.uuid,
      logId: exerciseState.logId,
      answer: answer.optionId
    };

    const correction = await api.exercise.submitDrillVocabularyAnswer(payload);

    return correction;
  }

  async onUserSubmitVocabularyPractice<V extends Vue>(
    component: V,
    exerciseState: ExerciseState,
    state: DrillState,
    answer: DrillVocabularyAnswer
  ): Promise<RespDrillAnswer> {
    const authInfo = await exerciseHandler.getAuthExerciseInfo(component, exerciseState);
    const payload: PayloadDrillListeningAnswer = {
      ...authInfo,
      uuid: answer.uuid,
      logId: exerciseState.logId,
      answer: answer.optionId
    };

    const correction = await api.exercise.submitDrillVocabularyPracticeAnswer(payload);

    return correction;
  }
}

export const drillHandler = new DrillHandler();
